import React, { FC, useContext } from "react";
import classNames from "classnames";
import { styles as TextStyes } from "~/components/Text";
import Container from "~/components/Container";
import { Identifiers } from "~/constants/enum";
import { FooterSSRContext } from "~/contexts/footer-ssr.context";
import useCmsBlocks from "~/hooks/useCmsBlocks";
import useClient from "~/hooks/useClient";
import { replaceClassNames } from "~/utils/converters";
import { isUsingLayoutV2 } from "~/utils/theme";
import type { FooterSSR } from "~/utils/cms-blocks-ssr";
import BlockByWidgetType from "~/sections/BlockByWidgetType/BlockByWidgetType";
import styles from "./Footer.module.scss";

interface FooterProps {
  footerSSR?: FooterSSR;
}

// Inlining the body of <FooterContent> here — defining it as a nested
// component would create a fresh function reference on every Footer render,
// which forces React to unmount and remount the entire subtree. That
// destroyed anything a third-party embed script had injected into a footer
// placeholder div (e.g. emk/Klaviyo forms).
const Footer: FC<FooterProps> = ({ footerSSR: footerSSRProp }) => {
  const isClient = useClient();
  const footerSSRCtx = useContext(FooterSSRContext);
  const footerSSR = footerSSRProp ?? footerSSRCtx ?? undefined;
  const hasSSR = !!(footerSSR?.footer || footerSSR?.copyRight);

  const { cmsBlocks, loading } = useCmsBlocks(
    hasSSR ? [] : [Identifiers.Footer, Identifiers.CopyRight]
  );

  const complieStyle = {
    Footer: styles,
    Text: TextStyes,
  };

  const copyRightArea =
    footerSSR?.copyRight || cmsBlocks[Identifiers.CopyRight]?.content;
  const footerArea =
    footerSSR?.footer || cmsBlocks[Identifiers.Footer]?.content;

  const content = hasSSR ? (
    <React.Fragment>
      {footerArea && (
        <BlockByWidgetType
          inputString={footerArea}
          prerenderedHtml={footerArea}
        />
      )}
      <div
        dangerouslySetInnerHTML={{
          __html: replaceClassNames(copyRightArea, complieStyle),
        }}
      />
    </React.Fragment>
  ) : loading || !cmsBlocks ? null : (
    <React.Fragment>
      {" "}
      {footerArea && isClient && (
        <BlockByWidgetType inputString={footerArea} />
      )}
      <div
        dangerouslySetInnerHTML={{
          __html: replaceClassNames(copyRightArea, complieStyle),
        }}
      />
    </React.Fragment>
  );

  return (
    <div
      className={classNames(styles.footer_wrapper, {
        "py-[2.5rem]": !isUsingLayoutV2,
      })}
    >
      {isUsingLayoutV2 ? (
        <div className={styles.footer}>{content}</div>
      ) : (
        <Container className={styles.footer}>{content}</Container>
      )}
      <div
        id="footer_ratingbadge"
        className="max-w-[500px] overflow-hidden"
      ></div>
    </div>
  );
};

export default Footer;
